/**
 * Microphone button component for voice input
 *
 * Shows a pulsing animation when recording with stop and cancel buttons
 */
import React from 'react';

interface MicrophoneButtonProps {
  isRecording: boolean;
  isTranscribing: boolean;
  waveformData?: number[];
  onClick: () => void;
  onCancel?: () => void;
  disabled?: boolean;
  wide?: boolean;
}

export const MicrophoneButton: React.FC<MicrophoneButtonProps> = ({
  isRecording,
  isTranscribing,
  waveformData = [],
  onClick,
  onCancel,
  disabled = false,
  wide = false,
}) => {
  const barCount = wide ? 56 : 28;
  const bars = waveformData.length > 0
    ? waveformData
    : Array.from({ length: barCount }, () => 0.06);

  // When recording, show stop and cancel buttons side by side
  if (isRecording) {
    return (
      <div className={`flex items-center gap-2 ${wide ? 'flex-1' : ''}`}>
        {/* Cancel button (X) - discard recording */}
        <button
          onClick={onCancel}
          className="p-3 rounded-full transition-all duration-200 bg-gray-200 text-gray-600 hover:bg-gray-300 cursor-pointer"
          title="Cancel recording"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Live waveform */}
        <div
          className={`h-12 rounded-full border border-red-200 bg-gradient-to-r from-red-50 via-white to-red-50 px-2 overflow-hidden ${wide ? 'flex-1' : 'w-24 sm:w-36 md:w-44'}`}
          title="Listening..."
        >
          <div className={`flex h-full items-center ${wide ? 'justify-between' : 'gap-[2px]'}`}>
            {bars.map((level, index) => {
              const height = Math.max(6, Math.round(level * 34) + 6);
              const opacity = Math.min(1, 0.45 + level * 0.65);

              return (
                <span
                  key={index}
                  className="w-1 rounded-full bg-red-500 transition-all duration-75 flex-shrink-0"
                  style={{ height: `${height}px`, opacity }}
                />
              );
            })}
          </div>
        </div>

        {/* Confirm button (checkmark) - stop and transcribe */}
        <button
          onClick={onClick}
          className="p-3 rounded-full transition-all duration-200 bg-[#ed7203] text-white hover:bg-[#c75f02] cursor-pointer"
          title="Stop recording"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </button>
      </div>
    );
  }

  // When transcribing, show spinner
  if (isTranscribing) {
    return (
      <button
        disabled
        className="p-3 rounded-full transition-all duration-200 bg-gray-100 text-gray-600 opacity-50 cursor-not-allowed"
        title="Transcribing..."
      >
        <svg className="w-6 h-6 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </button>
    );
  }

  // Default state - microphone icon
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        p-3 rounded-full transition-all duration-200
        bg-gray-100 text-gray-600 hover:bg-gray-200
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
      title="Spracheingabe"
    >
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
      </svg>
    </button>
  );
};
